/**
 * Horographia — 시계 기술: 원리 · 쓰임 · 역사 (교육용).
 */

export type TechTopic = {
  id: string;
  titleKo: string;
  titleEn: string;
  category: 'timekeeping' | 'case' | 'complication' | 'materials' | 'power';
  principle: string;
  uses: string[];
  history: string;
  relatedPieceIds: string[];
};

export const TECH_CATEGORIES: { id: TechTopic['category']; labelKo: string }[] = [
  { id: 'timekeeping', labelKo: '시간 분할' },
  { id: 'power', labelKo: '동력·와인딩' },
  { id: 'case', labelKo: '케이스·방수' },
  { id: 'complication', labelKo: '컴플리케이션' },
  { id: 'materials', labelKo: '소재·마감' },
];

export const techTopics: TechTopic[] = [
  {
    id: 'lever-escapement',
    titleKo: '레버 이스케이프먼트',
    titleEn: 'Swiss lever escapement',
    category: 'timekeeping',
    principle:
      '팔렛 포크가 이스케이프 휠의 이를 번갈아 잡고 놓으며, 밸런스에 충격(임펄스)을 전달한다. 기어열은 「똑딱」 단위로만 풀린다.',
    uses: ['대부분 기계식 손목시계의 표준', '안정적 대량 생산·정비 용이'],
    history: '18~19세기에 개량되어 스위스 레버가 사실상 표준이 되었다. 회중시계에서 손목시계로 그대로 이어졌다.',
    relatedPieceIds: ['submariner', 'datejust', 'pilot', 'portugueser'],
  },
  {
    id: 'balance-hairspring',
    titleKo: '밸런스와 헤어스프링',
    titleEn: 'Balance & hairspring',
    category: 'timekeeping',
    principle:
      '밸런스 휠의 관성 모멘트와 헤어스프링의 탄성으로 고유 진동수가 정해진다. 이 주기가 초를 나눈다.',
    uses: ['기계식 정확도의 핵심', '온도·자세 보정이 여기 집중'],
    history: '호이겐스 시대의 스프링 적용 이후, 니바록스 등 합금으로 자성·온도 영향을 줄여 왔다.',
    relatedPieceIds: ['calatrava', 'speedmaster', 'reverso'],
  },
  {
    id: 'quartz-oscillator',
    titleKo: '쿼츠 발진',
    titleEn: 'Quartz crystal oscillator',
    category: 'timekeeping',
    principle:
      '수정편이 특정 주파수로 진동하고, IC가 그 신호를 분주해 스텝 모터나 디지털 표시를 구동한다. 전지는 회로·모터에 전력을 공급한다.',
    uses: ['높은 일오차 안정성', '얇고 저렴한 양산', '솔라·키네틱 등 파생'],
    history: '1969년 세이코 아스트론 상용화 이후 「쿼츠 위기」로 기계식 산업이 재편되었다.',
    relatedPieceIds: ['tank', 'prospex'],
  },
  {
    id: 'co-axial',
    titleKo: '코액시얼 이스케이프먼트',
    titleEn: 'Co-Axial escapement',
    category: 'timekeeping',
    principle:
      '동축에 배치된 이빨로 슬라이딩 마찰을 줄이고 임펄스를 전달하려는 구조. 윤활 의존을 낮추는 것이 설계 목표로 알려져 있다.',
    uses: ['오메가 코액시얼 칼리버', '장기 안정·서비스 간격 마케팅과 연결'],
    history: '조지 대니얼스가 고안하고 오메가가 양산 채택했다. 레버를 대체하는 소수 경로 중 하나다.',
    relatedPieceIds: ['seamaster', 'constellation'],
  },
  {
    id: 'spring-drive',
    titleKo: '스프링드라이브',
    titleEn: 'Spring Drive',
    category: 'timekeeping',
    principle:
      '태엽이 역학 에너지를 공급하고, 전자기 브레이크와 IC가 속도를 제어한다. 초침이 끊기지 않고 흐르듯 움직인다.',
    uses: ['그랜드 세이코 등', '기계식 감성과 전자 제어의 결합'],
    history: '세이코가 연구·상용화. 쿼츠와도 전통 오토와도 다른 「제3의」 경로로 소개된다.',
    relatedPieceIds: ['grand-seiko', 'prospex'],
  },
  {
    id: 'automatic-winding',
    titleKo: '오토매틱 와인딩',
    titleEn: 'Automatic winding',
    category: 'power',
    principle:
      '로터가 회전하며 리버스 휠·클리치를 통해 배럴의 메인스프링을 감는다. 손목 운동이 동력이 된다.',
    uses: ['일상 착용 시 수동 감기를 줄임', '스포츠·툴 워치의 기본'],
    history: '20세기 초중반 실용화. 롤렉스 등 브랜드가 방수 케이스와 결합해 대중화했다.',
    relatedPieceIds: ['submariner', 'daytona', 'gmt-master', 'royal-oak'],
  },
  {
    id: 'manual-wind',
    titleKo: '수동 와인딩',
    titleEn: 'Manual winding',
    category: 'power',
    principle: '크라운으로 라칫·클릭을 거쳐 메인스프링을 직접 감는다. 구조가 단순하고 두께를 줄이기 쉽다.',
    uses: ['드레스·수동 크로노', '착용자가 태엽 상태를 의식하는 경험'],
    history: '회중시계부터의 기본. 오토매틱 등장 후에도 얇음·전통을 이유로 남았다.',
    relatedPieceIds: ['speedmaster', 'calatrava', 'reverso'],
  },
  {
    id: 'power-reserve-ind',
    titleKo: '파워 리저브 표시',
    titleEn: 'Power-reserve indicator',
    category: 'power',
    principle: '배럴이나 차동 장치에서 「감긴 양」을 뽑아 바늘·디스크에 보여 준다.',
    uses: ['장파워 모델에서 감기 시점 파악', '파일럿·포르투기저 등'],
    history: '회중·테이블 클락에서 손목시계로 이식. IWC 등에서 DAYS 스케일로 직관화했다.',
    relatedPieceIds: ['pilot', 'portugueser'],
  },
  {
    id: 'oyster-case',
    titleKo: '스크류 방수 케이스',
    titleEn: 'Screw-down waterproof case',
    category: 'case',
    principle:
      '케이스백·크라운 튜브를 나사로 조이고 가스켓으로 밀폐한다. 물이 침투할 경로를 길게·좁게 만든다.',
    uses: ['다이버·스포츠', '먼지·습기 차단'],
    history: '1920년대 오이스터로 상징되는 접근이 이후 산업 표준에 큰 영향을 주었다.',
    relatedPieceIds: ['submariner', 'daytona', 'gmt-master', 'datejust'],
  },
  {
    id: 'uni-bezel',
    titleKo: '단방향 회전 베젤',
    titleEn: 'Unidirectional rotating bezel',
    category: 'case',
    principle:
      '래칫으로 한쪽으로만 돌아가게 해, 충격으로 베젤이 밀려도 경과 시간이 실제보다 짧게만 읽히게 한다.',
    uses: ['스쿠버 다이빙 경과 시간', '쿨다운·주차 타이머 대용'],
    history: '1950년대 다이버 워치와 함께 정착. 서브마리너·시마스터·프로스펙스 등에 공통.',
    relatedPieceIds: ['submariner', 'seamaster', 'prospex', 'hydroconquest'],
  },
  {
    id: 'sapphire',
    titleKo: '사파이어 크리스탈',
    titleEn: 'Sapphire crystal',
    category: 'materials',
    principle: '합성 커런덤을 연마한 창. 모스 경도가 높아 일상 긁힘에 강하다. AR 코팅으로 반사를 줄인다.',
    uses: ['현대 대부분 중고가 시계', '전시백 윈도우'],
    history: '아크릴·미네랄 유리에서 1980~2000년대에 걸쳐 주류로 올라왔다. 일부 모델은 전통 아크릴을 유지한다.',
    relatedPieceIds: ['nautilus', 'royal-oak', 'overseas', 'constellation'],
  },
  {
    id: 'chronograph-tech',
    titleKo: '크로노그래프 기구',
    titleEn: 'Chronograph mechanism',
    category: 'complication',
    principle:
      '푸셔가 클러치·컬럼 휠(또는 캠)을 움직여 중앙 초침과 카운터를 기어열에 붙이거나 떼고, 해머로 리셋한다.',
    uses: ['레이싱·실험 타이밍', '타키미터와 조합'],
    history: '19세기 스톱워치에서 손목 크로노로. 데이토나·스피드마스터가 문화 아이콘이 되었다.',
    relatedPieceIds: ['daytona', 'speedmaster'],
  },
  {
    id: 'tachymeter-tech',
    titleKo: '타키미터 스케일',
    titleEn: 'Tachymeter scale',
    category: 'complication',
    principle:
      '기준 거리(보통 1 km/1 mile)를 달린 초를 베젤·챕터의 눈금에 대응시켜 평균 속도를 읽는다.',
    uses: ['모터스포츠', '분당 생산량 등 아날로그 환산'],
    history: '계측 다이얼의 전통이 손목 크로노 베젤로 옮겨졌다.',
    relatedPieceIds: ['daytona', 'speedmaster'],
  },
  {
    id: 'gmt-tech',
    titleKo: 'GMT 바늘',
    titleEn: 'GMT hand',
    category: 'complication',
    principle:
      '24시간에 한 바퀴 도는 추가 시침과 24시간 베젤·디스크로 두 번째 시간대를 표시한다. 로컬 시침만 독립 점프하는 방식도 있다.',
    uses: ['항공·여행', '본사·현지 시각 동시 확인'],
    history: '팬암 등 항공 수요와 함께 1950년대 GMT-Master가 상징이 되었다.',
    relatedPieceIds: ['gmt-master'],
  },
  {
    id: 'date-mech',
    titleKo: '날짜 디스크',
    titleEn: 'Date disc',
    category: 'complication',
    principle: '24시간에 한 칸씩 날짜 링을 밀어 창에 숫자를 보여 준다. 자정 전후 「천천히 바뀌는」 모델과 瞬时 날짜 변경 모델이 있다.',
    uses: ['일상 편의', '사이클로프스 확대'],
    history: '롤렉스 데이트저스트가 「항상 날짜가 보이는 손목시계」 이미지를 강화했다.',
    relatedPieceIds: ['datejust', 'submariner', 'seamaster'],
  },
  {
    id: 'flip-case',
    titleKo: '리버소 플립 케이스',
    titleEn: 'Reverso sliding case',
    category: 'case',
    principle: '케이스가 레일 위에서 미끄러지며 뒤집혀 크리스탈을 보호하거나 뒷면을 드러낸다.',
    uses: ['폴로·충격으로부터 유리 보호', '이니셜·두 번째 다이얼'],
    history: '1931년 예거 르쿨트르 리버소. 아르데코 비례와 함께  Longevity를 얻었다.',
    relatedPieceIds: ['reverso'],
  },
  {
    id: 'ceramic-bezel',
    titleKo: '세라믹 베젤 인서트',
    titleEn: 'Ceramic bezel insert',
    category: 'materials',
    principle: '지르코니아 등 세라믹을 소결·연마하고 눈금을 채워 넣는다. 자외선에 색이 잘 바래지 않는다.',
    uses: ['현대 다이버·GMT 베젤', '스크래치 내성'],
    history: '2000년대 이후 알루미늄 인서트를 대체하는 흐름이 커졌다.',
    relatedPieceIds: ['gmt-master', 'submariner', 'hydroconquest'],
  },
  {
    id: 'lume-tech',
    titleKo: '축광(루미)',
    titleEn: 'Photoluminescence',
    category: 'materials',
    principle: '빛 에너지를 흡수한 안료가 서서히 가시광으로 방출한다. 방사성 물질 없이도 야간 가독성을 얻는다.',
    uses: ['다이버·파일럿·필드', '침·인덱스·베젤 도트'],
    history: '라듐→트리튬→비방사성 Super-LumiNova류로 전환되며 안전과 밝기 균형이 달라졌다.',
    relatedPieceIds: ['pilot', 'submariner', 'prospex', 'hydroconquest'],
  },
];
